import { Form } from "react-final-form";
import { useState, useMemo } from "react";
import { format } from "date-fns";
import deepEqual from "deep-equal";

import { EditQuestion } from "../EditQuestion";
import { DisplayQuestion } from "../DisplayQuestion";
import { FormState } from "../types";

import { matchQuestionsToAnswers } from "./utils";
import { SubmitWrapper } from "./shared";

import { Button, Spacing, ErrorText } from "@otta/design";
import { SectionHeader } from "@otta/search/pages/Profile/components/FormHeader";
import {
  ApplicationData,
  JobApplicationDataInput,
  WorkableAnswerInput,
  WorkableAnswersFragment,
  WorkableApplicationQuestion,
} from "@otta/search/schema";
import { HeaderWrapper } from "@otta/search/pages/Profile/PublicProfile/components/Section";
import {
  Card,
  EditButton,
} from "@otta/search/pages/Profile/components/FormCard";
import { Pencil } from "@otta/search/components/Icons/Pencil";
import { Delete } from "@otta/search/components/Icons/Delete";

type WorkableFormState = FormState;

interface WorkableFormProps {
  questionData: {
    questions: WorkableApplicationQuestion[];
  };
  answerData?: {
    answers: ApplicationData.WorkableAnswerDataInlineFragment["answers"];
  };
  onSubmit: (input: JobApplicationDataInput) => Promise<void>;
}
export function WorkableForm({
  questionData,
  answerData = { answers: [] },
  onSubmit,
}: WorkableFormProps): React.ReactElement {
  const [isEditing, setIsEditing] = useState(false);

  const questions = questionData.questions;

  const answers = useMemo(
    () =>
      matchQuestionsToAnswers<
        WorkableApplicationQuestion[],
        WorkableAnswersFragment["answers"]
      >(questions, answerData.answers),
    [questions, answerData]
  );

  const initialValues = useMemo(
    () => ({
      answers,
    }),
    [answers]
  );

  const handleSubmit = async (input: WorkableFormState) => {
    await onSubmit(formatMutationData(input));
    setIsEditing(false);
  };

  return (
    <Card
      clickable={!isEditing}
      onClick={isEditing ? undefined : () => setIsEditing(true)}
      data-testid="application-questions-card"
    >
      <Spacing>
        <HeaderWrapper>
          <SectionHeader bold size={1}>
            Application questions
          </SectionHeader>
          <EditButton
            data-testid="edit-button"
            onClick={() => setIsEditing(false)}
          >
            {isEditing ? <Delete /> : <Pencil id="edit-button" />}
          </EditButton>
        </HeaderWrapper>
        {isEditing ? (
          <Form<WorkableFormState>
            onSubmit={handleSubmit}
            initialValues={initialValues}
            initialValuesEqual={(oldState, newState) =>
              deepEqual(oldState, newState)
            }
            render={({ handleSubmit, valid, submitFailed }) => (
              <form onSubmit={handleSubmit}>
                <Spacing>
                  {questions.map((q, index) => (
                    <EditQuestion
                      key={q.atsId}
                      question={q}
                      fieldName={`answers[${index}].value`}
                    />
                  ))}
                  <SubmitWrapper>
                    <ErrorText>
                      {!valid &&
                        submitFailed &&
                        "Some fields need your attention before you can save"}
                    </ErrorText>
                    <Button level="primary" onClick={handleSubmit}>
                      Save
                    </Button>
                  </SubmitWrapper>
                </Spacing>
              </form>
            )}
          />
        ) : (
          <Spacing>
            {questions.map((q, idx) => (
              <DisplayQuestion
                key={q.atsId}
                answer={answers[idx]}
                required={q.required}
              />
            ))}
          </Spacing>
        )}
      </Spacing>
    </Card>
  );
}

function formatMutationData({
  answers,
}: WorkableFormState): JobApplicationDataInput {
  return {
    workableData: {
      answers: answers.reduce<WorkableAnswerInput[]>((acc, answer) => {
        if (answer.value === null || answer.value === undefined) {
          return acc;
        }

        if (answer.questionType === "TextQuestion") {
          return [
            ...acc,
            {
              textAnswer: {
                questionData: answer.questionData,
                value: answer.value,
              },
            },
          ];
        }

        if (answer.questionType === "TextAreaQuestion") {
          return [
            ...acc,
            {
              textAreaAnswer: {
                questionData: answer.questionData,
                value: answer.value,
              },
            },
          ];
        }

        if (answer.questionType === "BooleanQuestion") {
          return [
            ...acc,
            {
              booleanAnswer: {
                questionData: answer.questionData,
                value: answer.value,
              },
            },
          ];
        }

        if (answer.questionType === "DateQuestion") {
          return [
            ...acc,
            {
              dateAnswer: {
                questionData: answer.questionData,
                value: format(answer.value, "yyyy-MM-dd"),
              },
            },
          ];
        }

        if (answer.questionType === "DecimalQuestion") {
          return [
            ...acc,
            {
              decimalAnswer: {
                questionData: answer.questionData,
                value: answer.value,
              },
            },
          ];
        }

        if (answer.questionType === "SingleSelectQuestion") {
          return [
            ...acc,
            {
              singleSelectAnswer: {
                questionData: answer.questionData,
                choice: answer.value,
              },
            },
          ];
        }

        if (answer.questionType === "MultiSelectQuestion") {
          return [
            ...acc,
            {
              multiSelectAnswer: {
                questionData: answer.questionData,
                choices: answer.value.map(choice => JSON.parse(choice)),
              },
            },
          ];
        }

        if (answer.questionType === "FileQuestion") {
          const fileAnswer =
            "file" in answer.value
              ? {
                  fileAnswer: {
                    questionData: answer.questionData,
                    name: answer.value.name,
                    file: answer.value.file,
                  },
                }
              : {
                  existingFileAnswer: {
                    questionData: answer.questionData,
                    name: answer.value.name,
                    url: answer.value.url,
                  },
                };

          return [...acc, fileAnswer];
        }
        return acc;
      }, []),
    },
  };
}
